// NOTE: hooks
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthContext } from './useAuthContext'
import { confirmCheckout } from '../api/health/stripe'

/**
 * Report the outcome of a checkout. Stripe returns the buyer to the page the purchase started from,
 * flagged with the outcome, which is cleared from the url once it has been shown.
 */
const useCheckoutResult = () => {
    const { user } = useAuthContext()
    const [searchParams, setSearchParams] = useSearchParams()
    const queryClient = useQueryClient()

    const outcome = searchParams.get('checkout')
    const sessionId = searchParams.get('session_id')

    const { mutate: confirmPayment } = useMutation({
        mutationFn: async (sessionId: string) => {
            const response = await confirmCheckout(sessionId, user.token)

            const json = await response.json()

            if (!response.ok) throw new Error(json.error)

            return json ?? null
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kits'] })
        },
        onError: (error: any) => {
            toast.error("Couldn't confirm your payment", { description: error.message })
        }
    })

    useEffect(() => {
        // NOTE: a checkout still to be started is not an outcome, and its flag is left for the investigation to act on
        if (outcome !== 'success' && outcome !== 'cancelled') return

        if (outcome === 'success') {
            toast.success('Payment received', { description: "We'll email you as soon as your order is on its way." })

            // NOTE: records the purchase now rather than waiting on the webhook, then reloads the investigations with it
            if (sessionId && user?.token) confirmPayment(sessionId)
        }

        if (outcome === 'cancelled') toast.info('Checkout cancelled', { description: 'Nothing has been charged.' })

        searchParams.delete('checkout')
        searchParams.delete('kit')
        searchParams.delete('session_id')
        setSearchParams(searchParams, { replace: true })
    }, [outcome, sessionId, user?.token, confirmPayment, searchParams, setSearchParams])
}

export default useCheckoutResult
